import { onUnmounted, ref } from "vue";

export interface TypewriterOptions {
  typeSpeed?: number;
  deleteSpeed?: number;
  holdTime?: number;
}

/**
 * Composable que alterna frases em looping com efeito de digitação e exclusão.
 *
 * @remarks
 * Reutiliza a lista original de mensagens e gerencia os timers internos,
 * limpando-os automaticamente em {@link onUnmounted}. Use `ref` reativo
 * `displayText` no template para renderizar o texto animado.
 *
 * @param messages - Lista de frases rotacionadas pelo efeito.
 * @param options - Configurações de timing (opcional).
 * @param options.typeSpeed - Tempo (ms) entre cada caractere digitado.
 * @param options.deleteSpeed - Tempo (ms) entre cada caractere excluído.
 * @param options.holdTime - Tempo (ms) que a frase completa fica visível antes de apagar.
 * @returns Estado reativo com o texto visível e o índice atual da frase.
 *
 * @example
 * ```typescript
 * const { displayText } = useTypewriter(["Primeira frase", "Segunda frase"]);
 * ```
 */
export function useTypewriter(
  messages: string[],
  options: TypewriterOptions = {}
) {
  const typeSpeed = options.typeSpeed ?? 55;
  const deleteSpeed = options.deleteSpeed ?? 28;
  const holdTime = options.holdTime ?? 1800;

  const displayText = ref("");
  const currentIndex = ref(0);
  const isComplete = ref(false);

  let typingTimer: ReturnType<typeof setTimeout> | null = null;
  let deletingTimer: ReturnType<typeof setTimeout> | null = null;
  let holdTimer: ReturnType<typeof setTimeout> | null = null;
  let phaseTimer: ReturnType<typeof setTimeout> | null = null;

  function clearAllTimers(): void {
    if (typingTimer) clearTimeout(typingTimer);
    if (deletingTimer) clearTimeout(deletingTimer);
    if (holdTimer) clearTimeout(holdTimer);
    if (phaseTimer) clearTimeout(phaseTimer);
  }

  function typeNext(): void {
    const fullText = messages[currentIndex.value] ?? "";
    const currentLength = displayText.value.length;

    if (currentLength < fullText.length) {
      displayText.value = fullText.slice(0, currentLength + 1);
      typingTimer = setTimeout(typeNext, typeSpeed);
    } else {
      isComplete.value = true;
      holdTimer = setTimeout(startDeleting, holdTime);
    }
  }

  function startDeleting( nextIndex?: number ): void {
    isComplete.value = false;
    deletingTimer = setInterval(() => {
      const currentLength = displayText.value.length;
      if (currentLength > 0) {
        displayText.value = displayText.value.slice(0, -1);
      } else {
        clearInterval(deletingTimer as unknown as number);
        deletingTimer = null;
        currentIndex.value = nextIndex ?? (currentIndex.value + 1) % messages.length;
        typingTimer = setTimeout(typeNext, typeSpeed);
      }
    }, deleteSpeed);
  }

  function start(): void {
    clearAllTimers();
    displayText.value = "";
    isComplete.value = false;
    typingTimer = setTimeout(typeNext, typeSpeed);
  }

  onUnmounted(clearAllTimers);

  return { displayText, isComplete, start };
}